import { RefObject, useCallback, useRef } from 'react';
import { MarkerData } from '../classes/MarkerData';
import { isPointContainedInBounds } from '../helpers/helper';

const useVisibleMarkers = (markers: MarkerData[], mapRef: RefObject<$TSFixMe>) => {
  const visibleMarkersSet = useRef(new Set());

  const clearMarkers = useCallback(() => {
    visibleMarkersSet.current?.clear();
  }, []);

  const createMarkerKey = useCallback((marker: MarkerData) => {
    return `${marker.location[0]}_${marker.location[1]}`;
  }, []);

  const validateNewMarkers = useCallback(
    (newMarkers: MarkerData[]): boolean => {
      if (newMarkers.length !== visibleMarkersSet.current.size) {
        return true;
      }
      let existCounter = 0;
      for (const marker of newMarkers) {
        if (visibleMarkersSet.current.has(createMarkerKey(marker))) {
          existCounter++;
        }
      }
      return existCounter !== visibleMarkersSet.current.size;
    },
    [createMarkerKey]
  );

  const assignNewVisibleMarkers = useCallback(
    (newMarkers: MarkerData[]) => {
      visibleMarkersSet.current.clear();
      for (const marker of newMarkers) {
        visibleMarkersSet.current.add(createMarkerKey(marker));
      }
    },
    [createMarkerKey]
  );

  const getVisibleMarkers = useCallback(
    (markers: MarkerData[]) => {
      const mainMarkers = markers.filter((marker) =>
        isPointContainedInBounds(marker.location[0], marker.location[1], mapRef)
      );
      const leftMarkers = markers.filter((marker) =>
        isPointContainedInBounds(marker.location[0], marker.location[1] - 360, mapRef)
      );
      const rightMarkers = markers.filter((marker) =>
        isPointContainedInBounds(marker.location[0], marker.location[1] + 360, mapRef)
      );

      return [...mainMarkers, ...leftMarkers, ...rightMarkers];
    },
    [mapRef]
  );

  const getNewVisibleMarkers: () => Promise<MarkerData[]> = useCallback(() => {
    return new Promise((resolve) => {
      const visibleMarkers = getVisibleMarkers(markers);
      if (validateNewMarkers(visibleMarkers)) {
        assignNewVisibleMarkers(visibleMarkers);
        return resolve(visibleMarkers);
      }
    });
  }, [assignNewVisibleMarkers, getVisibleMarkers, markers, validateNewMarkers]);

  return {
    clearMarkers,
    getNewVisibleMarkers
  };
};

export default useVisibleMarkers;
